import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import { addMonths, startOfMonth } from "date-fns";
import { format } from "date-fns";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, bool, date, object, string } from "yup";
import { number } from "../Utils/schemaUtils";

export type PensionGoalFormType = {
  readonly name: string;
  readonly infinitePension: boolean;
  readonly includeStatePension: boolean;
  readonly privatePensionStartAge: number;
  readonly privatePensionStartAgeMonths?: number;
  readonly privatePensionStartDate?: Date | null;
  readonly privatePensionDuration: number;
  readonly yearsWorked?: number; // optional if
  readonly netIncomeAmount?: number; // optional if
  readonly grossIncomeAmount?: number;
  readonly statePensionAmount?: number; // optional if
  readonly statePensionStart?: Date | null; // optional if
  readonly referenceStartDate: Date;
  readonly privatePensionAmount: number;
  readonly targetAmount: number;
  readonly monthlyContributionWithFee: number;
  readonly pensionInterestRate: number;
  readonly interestRate: number;
  readonly inflationRate: number;
  readonly feeRate: number;
  readonly includeInflation?: boolean;
  readonly includeFee?: boolean;
  readonly isTargetAmountManual?: boolean;
  readonly isInvestmentAmountManual?: boolean;
  readonly note?: string;
  readonly priority?: boolean;
  readonly _empty?: Date;
};

export const minDate = startOfMonth(addMonths(new Date(), 1));
export const minRetirementDate = startOfMonth(new Date());

export type PensionGoalFormContext = {
  readonly hasClientBirthDate: boolean;
};

export const getPensionGoalFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<PensionGoalFormType> = (t) => {
  return object({
    name: string().required(t(Resources.Validation.Required)),
    infinitePension: bool().required(t(Resources.Validation.Required)),
    includeStatePension: bool().required(t(Resources.Validation.Required)),
    privatePensionStartAge: number().required(t(Resources.Validation.Required)),
    privatePensionStartAgeMonths: number(),
    // Without a birth date the retirement date can't be derived and the field is disabled — the age alone is enough.
    privatePensionStartDate: date()
      .nullable()
      .when("$hasClientBirthDate", ([hasClientBirthDate], schema) =>
        hasClientBirthDate ? schema.required(t(Resources.Validation.Required)) : schema,
      )
      .min(minRetirementDate, t(Resources.Validation.DateMustNotBeInPast)),
    privatePensionDuration: number().required(t(Resources.Validation.Required)),
    yearsWorked: number(),
    netIncomeAmount: number().when("includeStatePension", (includeStatePension, schema) => {
      return includeStatePension[0]
        ? schema.min(1, t(Resources.Validation.Number.MoreThen, { count: 0 })).required(t(Resources.Validation.Required))
        : schema;
    }),
    // Derived from the net salary and not shown — it must never be the reason a save is blocked.
    grossIncomeAmount: number(),
    statePensionAmount: number().when("includeStatePension", (includeStatePension, schema) => {
      return includeStatePension[0] ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    statePensionStart: date().when("includeStatePension", (includeStatePension, schema) => {
      return includeStatePension[0]
        ? schema
            .required(t(Resources.Validation.Required))
            .min(minDate, t(Resources.Validation.MustBeOlderThanDate, { date: format(minDate, "dd.MM.yyyy") }))
        : schema;
    }),
    privatePensionAmount: number().required(t(Resources.Validation.Required)),
    targetAmount: number().required(t(Resources.Validation.Required)),
    monthlyContributionWithFee: number().required(t(Resources.Validation.Required)),
    interestRate: number().required(t(Resources.Validation.Required)),
    inflationRate: number().required(t(Resources.Validation.Required)),
    pensionInterestRate: number().required(t(Resources.Validation.Required)),
    referenceStartDate: date().required(t(Resources.Validation.Required)),
    feeRate: number().required(t(Resources.Validation.Required)),
    note: string().max(NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: NOTE_MAX_LENGTH })),
    priority: bool(),
    includeInflation: bool(),
    includeFee: bool(),
    isTargetAmountManual: bool(),
    isInvestmentAmountManual: bool(),
    _empty: date(),
  }).defined();
};
